import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useT } from "../../lib/i18n";
import { api } from "../../lib/api";
import {
  LayoutDashboard, Search, Megaphone, Wand2, Image as ImageIcon, FileText,
  Sparkles, Plug, Zap, BarChart3, Settings as SettingsIcon, Bot, ShieldCheck,
  Activity, LineChart, Euro, Gauge, BrainCircuit, Network, Rocket,
  Compass, Users, Target, Home as HomeIcon,
} from "lucide-react";

// Phase 2 (Feb 2026) — Flat 5-point primary nav on Home first, followed
// by the legacy detail-nav (kept for owner power-users). See concept
// §5 in /app/docs/concept/frontend_v2/01_final_concept.md.
const PRIMARY_NAV = [
  { to: "/app/home", icon: HomeIcon, key: "nav.home", testid: "nav-home" },
  { to: "/app/campaigns", icon: Megaphone, key: "nav.campaigns", testid: "nav-campaigns" },
  { to: "/app/leads", icon: Users, key: "nav.leads", testid: "nav-leads" },
  { to: "/app/content", icon: Sparkles, key: "nav.content", testid: "nav-content" },
  { to: "/app/settings", icon: SettingsIcon, key: "nav.settings", testid: "nav-settings" },
];

// Phase 10.2 — new "Intelligence" section fronting the tenant's learning stack.
// TODO(phase-10.3): promote a top-level "Knowledge Graph" entry once the
// dedicated `/app/knowledge-graph` explorer route lands. Today the KG is only
// reachable from Campaign detail → the KG drawer, and from the "View in KG"
// button on a Strategy Memory entry.
const NAV = [
  { to: "/app/dashboard", icon: LayoutDashboard, key: "nav.dashboard", testid: "nav-dashboard" },
  { to: "/app/cockpit", icon: LineChart, key: "nav.cockpit", testid: "nav-cockpit" },
  { to: "/app/ad-spend", icon: Euro, key: "nav.ad_spend", testid: "nav-ad-spend" },
  { to: "/app/research", icon: Search, key: "nav.research", testid: "nav-research", ai: true },
  { to: "/app/assistant", icon: Bot, key: "nav.assistant", testid: "nav-assistant", ai: true },
  { to: "/app/campaigns", icon: Megaphone, key: "nav.campaigns", testid: "nav-campaigns" },
  { to: "/app/approvals", icon: ShieldCheck, key: "nav.approvals", testid: "nav-approvals",
    badge: "pending_approvals" },
  { to: "/app/approval-chains", icon: ShieldCheck, key: "nav.approval_chains", testid: "nav-approval-chains" },
  { to: "/app/creative", icon: Wand2, key: "nav.creative", testid: "nav-creative", ai: true },
  { to: "/app/discovery-growth", icon: Compass, key: "nav.discovery_growth", testid: "nav-discovery-growth", ai: true },
  { to: "/app/leads", icon: Users, key: "nav.leads", testid: "nav-leads" },
  { to: "/app/goals", icon: Target, key: "nav.goals", testid: "nav-goals" },
  { to: "/app/assets", icon: ImageIcon, key: "nav.assets", testid: "nav-assets" },
  { to: "/app/landing-pages", icon: FileText, key: "nav.landing_pages", testid: "nav-landing-pages", ai: true },
  { to: "/app/content", icon: Sparkles, key: "nav.content", testid: "nav-content" },
  { to: "/app/integrations", icon: Plug, key: "nav.integrations", testid: "nav-integrations" },
  { to: "/app/integrations/ad-platforms", icon: Rocket, key: "nav.ad_platforms", testid: "nav-ad-platforms" },
  { to: "/app/autopilot", icon: Zap, key: "nav.autopilot", testid: "nav-autopilot",
    badge: "pending_autopilot_actions" },
  { to: "/app/reports", icon: BarChart3, key: "nav.reports", testid: "nav-reports", ai: true },
  { to: "/app/workflows", icon: Activity, key: "nav.workflows", testid: "nav-workflows" },
  { to: "/app/quality", icon: Gauge, key: "nav.quality", testid: "nav-quality", ai: true },
  { to: "/app/strategy-memory", icon: BrainCircuit, key: "nav.strategy_memory", testid: "nav-strategy-memory", ai: true },
  { to: "/app/knowledge-graph", icon: Network, key: "nav.knowledge_graph", testid: "nav-knowledge-graph", ai: true },
  { to: "/app/settings", icon: SettingsIcon, key: "nav.settings", testid: "nav-settings" },
];

function usePendingApprovals() {
  const [count, setCount] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/approvals/pending-count")
      .then((r) => { if (alive) setCount(r.data?.count || 0); })
      .catch(() => {});
    tick();
    const i = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(i); };
  }, [loc.pathname]);
  return count;
}

function usePendingAutopilotActions() {
  const [count, setCount] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/autopilot/actions/pending-count", { skipPermissionToast: true })
      .then((r) => { if (alive) setCount(r.data?.count || 0); })
      .catch(() => {});
    tick();
    const i = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(i); };
  }, [loc.pathname]);
  return count;
}

export default function Sidebar() {
  const loc = useLocation();
  const t = useT();
  const pending = usePendingApprovals();
  const pendingAutopilot = usePendingAutopilotActions();

  return (
    <aside data-testid="app-sidebar" className="w-64 shrink-0 border-r bg-card/40 backdrop-blur supports-[backdrop-filter]:bg-card/30 hidden md:flex md:flex-col">
      <div className="px-6 pt-6 pb-4">
        <Link to="/app/home" className="flex items-center gap-2" data-testid="brand-link">
          <div className="size-9 rounded-md bg-foreground text-background grid place-items-center font-semibold">V</div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-tight">VIONA</div>
            <div className="text-xs text-muted-foreground">Marketing OS</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto pb-4">
        <div className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-wider text-muted-foreground"
             data-testid="nav-section-primary">
          Übersicht
        </div>
        {PRIMARY_NAV.map(({ to, icon: Icon, key, testid }) => {
          const active = loc.pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              className={[
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                active
                  ? "bg-accent text-accent-foreground font-medium"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              ].join(" ")}
            >
              <Icon className="size-4" />
              <span className="flex-1">{t(key)}</span>
            </Link>
          );
        })}
        <div className="px-3 pt-4 pb-2 text-[10px] uppercase tracking-wider text-muted-foreground"
             data-testid="nav-section-advanced">
          Weitere Werkzeuge
        </div>
        {NAV.map(({ to, icon: Icon, key, testid, badge, ai }) => {
          const active = loc.pathname.startsWith(to);
          const showApprovalBadge = badge === "pending_approvals" && pending > 0;
          const showAutopilotBadge = badge === "pending_autopilot_actions" && pendingAutopilot > 0;
          return (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              className={[
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                active
                  ? "bg-accent text-accent-foreground font-medium"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              ].join(" ")}
            >
              <Icon className="size-4" />
              <span className="flex-1">{t(key)}</span>
              {ai && (
                <span
                  data-testid={`${testid}-ai-chip`}
                  className="inline-flex items-center text-[9px] font-semibold tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200"
                >
                  AI
                </span>
              )}
              {showApprovalBadge && (
                <span
                  data-testid="sidebar-approvals-badge"
                  className="inline-flex items-center justify-center text-[10px] font-medium min-w-[20px] h-5 px-1.5 rounded-full bg-foreground text-background"
                >
                  {pending}
                </span>
              )}
              {showAutopilotBadge && (
                <span
                  data-testid="sidebar-autopilot-badge"
                  className="inline-flex items-center justify-center text-[10px] font-medium min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-amber-50"
                >
                  {pendingAutopilot}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="px-6 py-4 text-xs text-muted-foreground border-t">
        v0.4.1 · Phase 4.1
      </div>
    </aside>
  );
}
